/**
 * A person, as a small circle.
 *
 * Initials only: `public.profiles` has no `avatar_url` and there is no storage
 * bucket behind one, so an image variant would be a prop nothing can ever fill.
 * The tint is derived from the name so two colleagues on the same card stay
 * distinguishable without anyone choosing a colour.
 */

interface Props {
  /** Null for someone outside the organization's directory — falls back to "?". */
  name: string | null;
  /** Accessible label. Pass `null` when an adjacent line already names them. */
  label?: string | null;
  className?: string;
  /** Custom background color (e.g. hex string). When omitted, derived from name. */
  color?: string | null;
}

const TINTS = [
  'bg-module-folders-soft text-module-folders',
  'bg-module-sheets-soft text-module-sheets',
  'bg-module-notes-soft text-module-notes',
  'bg-module-investments-soft text-module-investments',
  'bg-module-cashflow-soft text-module-cashflow',
  'bg-module-forms-soft text-module-forms',
] as const;

export function initialsOf(name: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const first = [...words[0]!][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]!][0] ?? '') : '';
  return (first + last).toUpperCase();
}

export function getContrastTextColor(hexColor: string): string {
  const clean = hexColor.replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  if (Number.isNaN(num) || full.length !== 6) return '#FFFFFF';
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? '#111827' : '#FFFFFF';
}

function tintFor(name: string | null): string {
  if (!name) return 'bg-surface-3 text-muted';
  let hash = 0;
  for (const char of name) hash = (hash + char.codePointAt(0)!) % TINTS.length;
  return TINTS[hash]!;
}

export function Avatar({ name, label, className = 'h-6 w-6 text-[0.625rem]', color }: Props) {
  const style = color
    ? { backgroundColor: color, color: getContrastTextColor(color) }
    : undefined;

  return (
    <span
      {...(label === null ? { 'aria-hidden': true } : { title: label ?? name ?? undefined })}
      style={style}
      className={`flex shrink-0 items-center justify-center rounded-full font-semibold ${color ? '' : tintFor(name)} ${className}`}
    >
      {initialsOf(name)}
    </span>
  );
}
